import { useState, useEffect, useRef } from "react";

export default function Achievements() {
  const [counters, setCounters] = useState({
    totalAwards: 0,
    goldMedals: 0,
    silverMedals: 0,
    bronzeMedals: 0,
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  const achievements = [
    {
      year: "2023",
      title: "Huy chương Vàng IMO",
      student: "Nguyễn Minh Anh",
      description: "Giải Nhất Olympic Toán học Quốc tế tại Nhật Bản",
      medal: "gold",
    },
    {
      year: "2023",
      title: "Huy chương Bạc IOI",
      student: "Trần Đức Minh",
      description: "Giải Nhì Olympic Tin học Quốc tế tại Hungary",
      medal: "silver",
    },
    {
      year: "2022",
      title: "Huy chương Vàng VMO",
      student: "Lê Thị Hương",
      description: "Giải Nhất Olympic Toán học Việt Nam",
      medal: "gold",
    },
    {
      year: "2022",
      title: "Huy chương Đồng APIO",
      student: "Phạm Văn Nam",
      description: "Giải Ba Olympic Tin học Châu Á - Thái Bình Dương",
      medal: "bronze",
    },
    {
      year: "2021",
      title: "Huy chương Bạc IMO",
      student: "Hoàng Thị Lan",
      description: "Giải Nhì Olympic Toán học Quốc tế tại Nga",
      medal: "silver",
    },
    {
      year: "2021",
      title: "Huy chương Vàng VOI",
      student: "Đỗ Minh Quân",
      description: "Giải Nhất Olympic Tin học Việt Nam",
      medal: "gold",
    },
  ];

  const getMedalColor = (medal: string) => {
    switch (medal) {
      case "gold":
        return "from-yellow-400 to-yellow-600";
      case "silver":
        return "from-gray-300 to-gray-500";
      case "bronze":
        return "from-orange-400 to-orange-600";
      default:
        return "from-blue-400 to-blue-600";
    }
  };

  const getMedalIcon = (medal: string) => {
    return "ri-medal-line";
  };
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const animateCounter = (
      target: number,
      key: keyof typeof counters,
      duration: number = 2000
    ) => {
      const startTime = Date.now();
      const startValue = 0;

      const updateCounter = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function for smooth animation
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.floor(
          startValue + (target - startValue) * easeOutCubic
        );

        setCounters((prev) => ({
          ...prev,
          [key]: currentValue,
        }));

        if (progress < 1) {
          requestAnimationFrame(updateCounter);
        }
      };

      requestAnimationFrame(updateCounter);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);

            // Start animations with slight delays for better visual effect
            setTimeout(() => animateCounter(50, "totalAwards", 2000), 300);
            setTimeout(() => animateCounter(15, "goldMedals", 2200), 500);
            setTimeout(() => animateCounter(20, "silverMedals", 2400), 700);
            setTimeout(() => animateCounter(15, "bronzeMedals", 2600), 900);
          }
        });
      },
      { threshold: 0.2 }
    );

    const cardsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    if (cardsRef.current) {
      cardsObserver.observe(cardsRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      if (cardsRef.current) {
        cardsObserver.unobserve(cardsRef.current);
      }
    };
  }, [hasAnimated]);

  return (
    <section id="achievements" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Thành tích nổi bật
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Những thành tích xuất sắc của học sinh Học viện Olympic trong các
              kỳ thi quốc gia và quốc tế
            </p>
          </div>

          <div
            ref={cardsRef}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 perspective-1000"
          >
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className={`bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-700 transform hover:-translate-y-2 overflow-hidden ${
                  isVisible
                    ? "translate-y-0 opacity-100 rotate-y-0"
                    : "translate-y-12 opacity-0 rotate-y-180"
                }`}
                style={{
                  transitionDelay: `${index * 150}ms`,
                  transformStyle: "preserve-3d",
                }}
              >
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-semibold">
                      {achievement.year}
                    </span>
                    <div
                      className={`w-12 h-12 bg-gradient-to-br ${getMedalColor(
                        achievement.medal
                      )} rounded-full flex items-center justify-center transform transition-transform duration-500 hover:rotate-180`}
                    >
                      <i
                        className={`${getMedalIcon(
                          achievement.medal
                        )} text-white text-xl`}
                      ></i>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {achievement.title}
                  </h3>

                  <p className="text-lg font-semibold text-blue-600 mb-3">
                    {achievement.student}
                  </p>

                  <p className="text-gray-600 leading-relaxed">
                    {achievement.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div
            ref={sectionRef}
            className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-white text-center"
          >
            <h3 className="text-3xl md:text-4xl font-bold mb-6">
              Thống kê thành tích tổng thể
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="transform transition-all duration-500 hover:scale-105">
                <div className="text-5xl md:text-6xl font-bold mb-2 font-mono text-yellow-300">
                  {counters.totalAwards}+
                </div>
                <div className="text-blue-100 text-lg">Giải thưởng Olympic</div>
              </div>
              <div className="transform transition-all duration-500 hover:scale-105">
                <div className="text-5xl md:text-6xl font-bold mb-2 font-mono text-yellow-300">
                  {counters.goldMedals}
                </div>
                <div className="text-blue-100 text-lg">Huy chương Vàng</div>
              </div>
              <div className="transform transition-all duration-500 hover:scale-105">
                <div className="text-5xl md:text-6xl font-bold mb-2 font-mono text-yellow-300">
                  {counters.silverMedals}
                </div>
                <div className="text-blue-100 text-lg">Huy chương Bạc</div>
              </div>
              <div className="transform transition-all duration-500 hover:scale-105">
                <div className="text-5xl md:text-6xl font-bold mb-2 font-mono text-yellow-300">
                  {counters.bronzeMedals}
                </div>
                <div className="text-blue-100 text-lg">Huy chương Đồng</div>
              </div>
            </div>

            <div className="mt-8">
              <p className="text-xl text-blue-100 mb-6">
                Hơn 95% học sinh của chúng tôi đạt giải trong các kỳ thi Olympic
              </p>
              <button
                onClick={() => scrollToSection("contact")}
                className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Đăng ký học thử miễn phí
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
